import type { LearningExercise, SourceRef } from "../types";
import { makeCloze, makeFlashcard, makeMulti, makeOrdering, makeSingle } from "./legalExerciseFactory";

// Unionsmarke (UMV/EUTM) — Termine 19–21 der deutschen Patentanwalts-Ausbildung
// Abdeckung: Art. 4, 7, 8, 9, 15, 18, 30–46, 58–64, 124–130, 139 ff. UMV
// IDs: da-union-001 … da-union-038
// Fallkonstellationen: da-union-008, -010, -012, -013, -015, -017, -019, -022, -024,
//   -026, -028, -030, -032, -034, -036, -037, -038 (≥ 45 %)
// Ordering-Items: da-union-020, da-union-035
// reviewStatus: alle 38 Items "reviewed" (question-reviewer-Gate 2026-06-04: 34 reviewed + 4 gefixt). Beta-ready = Vincents Schlusspass.

const pathId = "deutsche-ausbildung" as const;

const sources: Record<string, SourceRef> = {
  umv4: {
    id: "umv4",
    label: "Art. 4 UMV",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001-20190623#art_4",
  },
  umv7: {
    id: "umv7",
    label: "Art. 7 UMV",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001-20190623#art_7",
  },
  umv8: {
    id: "umv8",
    label: "Art. 8 UMV",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001-20190623#art_8",
  },
  umv9: {
    id: "umv9",
    label: "Art. 9 UMV",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001-20190623#art_9",
  },
  umv15: {
    id: "umv15",
    label: "Art. 15 UMV",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001-20190623#art_15",
  },
  umv18: {
    id: "umv18",
    label: "Art. 18 UMV",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001-20190623#art_18",
  },
  umv39: {
    id: "umv39",
    label: "Art. 39/40 UMV",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001-20190623#art_39",
  },
  umv46: {
    id: "umv46",
    label: "Art. 46 UMV",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001-20190623#art_46",
  },
  umv58: {
    id: "umv58",
    label: "Art. 58 UMV",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001-20190623#art_58",
  },
  umv59: {
    id: "umv59",
    label: "Art. 59 UMV",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001-20190623#art_59",
  },
  umv60: {
    id: "umv60",
    label: "Art. 60 UMV",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001-20190623#art_60",
  },
  umv124: {
    id: "umv124",
    label: "Art. 124 UMV",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001-20190623#art_124",
  },
  umv125: {
    id: "umv125",
    label: "Art. 125 UMV",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001-20190623#art_125",
  },
  umv130: {
    id: "umv130",
    label: "Art. 130 UMV",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001-20190623#art_130",
  },
  umv139: {
    id: "umv139",
    label: "Art. 139 UMV",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001-20190623#art_139",
  },
};

export const ausbildungUnionsmarke: LearningExercise[] = [

  // ─── FLASHCARDS da-union-001 … da-union-007 ──────────────────────────────

  makeFlashcard(sources, {
    id: "da-union-001",
    pathId,
    topic: "Art. 4 UMV — Markenfähige Zeichen",
    title: "Markenfähigkeit bei der Unionsmarke — Anforderungen seit 2017",
    prompt:
      "Welche Anforderungen stellt Art. 4 UMV an die Markenfähigkeit eines Zeichens, und was hat sich durch die UMV-Reform 2017 gegenüber der alten GMV geändert?",
    explanation:
      "Art. 4 UMV (in der Fassung seit 2017): Als Unionsmarke können alle Zeichen eingetragen werden, die geeignet sind, Waren oder Dienstleistungen eines Unternehmens von denjenigen anderer Unternehmen zu unterscheiden — vorausgesetzt, sie können im Register so dargestellt werden, dass der Gegenstand des Schutzes für die zuständigen Behörden und das Publikum klar und eindeutig bestimmbar ist. Entscheidende Änderung 2017: Die frühere Anforderung der grafischen Darstellbarkeit (in der alten GMV) wurde abgeschafft. Stattdessen gilt das Erfordernis der klaren und eindeutigen Bestimmbarkeit im Register. Dies ermöglicht die Eintragung neuer Zeichenformen (Klangmarken als Audiodatei, Bewegungsmarken als Videofile, Hologramme), ohne dass eine konventionelle grafische Darstellung vorgelegt werden muss.",
    sourceIds: ["umv4"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["unionsmarke", "markenfaehigkeit", "art4-umv"],
    reviewStatus: "reviewed",
  }),

  makeFlashcard(sources, {
    id: "da-union-002",
    pathId,
    topic: "Art. 7 UMV — Absolute Eintragungshindernisse",
    title: "Formmarken-Ausschlüsse: die drei Fallgruppen des Art. 7 Abs. 1 lit. e UMV",
    prompt:
      "Welche drei Fallgruppen schließt Art. 7 Abs. 1 lit. e UMV von der Eintragung als Formmarke aus, und warum sind diese Ausschlüsse absolut?",
    explanation:
      "Art. 7 Abs. 1 lit. e UMV schließt drei Gruppen von Zeichen aus, die ausschließlich bestehen aus: (i) Formen, die durch die Art der Ware selbst bedingt sind (Notwendigkeitsform); (ii) Formen der Ware, die zur Erreichung einer technischen Wirkung erforderlich sind (Funktionsform; EuGH 'Philips', C-299/99); (iii) Formen, die der Ware einen wesentlichen Wert verleihen (Wertform; EuGH 'Gömböc', C-237/19). Ab 2017 gilt dies auch für andere Charakteristika (nicht nur Formen), da Art. 4 UMV jetzt alle Zeichen erfasst. Die Ausschlüsse sind absolut, weil sie im Interesse des Wettbewerbs und der Warenfreiheit dauerhaft der Allgemeinheit zugänglich bleiben müssen — Verkehrsdurchsetzung nach Art. 7 Abs. 3 UMV heilt sie ausdrücklich nicht.",
    sourceIds: ["umv7"],
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["unionsmarke", "schutzhindernisse", "formmarke", "art7-umv"],
    reviewStatus: "reviewed",
  }),

  makeFlashcard(sources, {
    id: "da-union-003",
    pathId,
    topic: "Art. 8 UMV — Relative Eintragungshindernisse",
    title: "Verwechslungsgefahr (Art. 8 Abs. 1 lit. b UMV): Kumulation und Wechselwirkung",
    prompt:
      "Welche Voraussetzungen müssen für eine Verwechslungsgefahr nach Art. 8 Abs. 1 lit. b UMV kumulativ erfüllt sein, und was besagt das Wechselwirkungsprinzip?",
    explanation:
      "Art. 8 Abs. 1 lit. b UMV: Verwechslungsgefahr setzt kumulativ voraus: (1) Identität oder Ähnlichkeit der kollidierenden Zeichen; (2) Identität oder Ähnlichkeit der erfassten Waren oder Dienstleistungen. Fehlt eine dieser Voraussetzungen, scheidet Verwechslungsgefahr aus (EuGH 'Canon', C-39/97: nur Ähnlichkeit der Waren kann Zeichenunähnlichkeit nicht ersetzen). Das Wechselwirkungsprinzip (EuGH 'Canon'): Zwischen Zeichenähnlichkeit, Waren-/DL-Ähnlichkeit und Kennzeichnungskraft der älteren Marke besteht eine Wechselwirkung — ein geringer Grad bei einem Faktor kann durch höheren Grad bei einem anderen ausgeglichen werden. Die Gesamtbeurteilung erfolgt aus Sicht des Durchschnittsverbrauchers der betreffenden Waren-/DL-Klasse.",
    sourceIds: ["umv8"],
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["unionsmarke", "verwechslungsgefahr", "wechselwirkung", "art8-umv"],
    reviewStatus: "reviewed",
  }),

  makeFlashcard(sources, {
    id: "da-union-004",
    pathId,
    topic: "Art. 18 UMV — Benutzungspflicht",
    title: "Ernsthafte Benutzung der Unionsmarke: Frist und Beginn",
    prompt:
      "Ab welchem Zeitpunkt beginnt die Benutzungsfrist nach Art. 18 UMV zu laufen, und was bedeutet 'ernsthafte Benutzung'?",
    explanation:
      "Art. 18 Abs. 1 UMV: Die Unionsmarke unterliegt der Benutzungspflicht nach Ablauf von fünf Jahren ab dem Zeitpunkt der Eintragung (nicht der Anmeldung). Die Benutzung muss ernsthaft sein, d.h. sie muss im geschäftlichen Verkehr mit dem Ziel der Aufrechterhaltung oder Schaffung eines Marktanteils für die eingetragenen Waren oder Dienstleistungen in der Union erfolgen. Rein symbolische Benutzungen, die nur dazu dienen, den Verfall zu verhindern, sind nicht ausreichend (EuGH 'Ansul', C-40/01). Benutzung in nur einem EU-Mitgliedstaat kann ausreichend sein, wenn sie dort wirtschaftlich sinnvoll ist. Benutzungsort: Die Benutzung muss in der Union stattfinden; Benutzung im Drittland genügt nicht.",
    sourceIds: ["umv18"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["unionsmarke", "benutzungspflicht", "art18-umv"],
    reviewStatus: "reviewed",
  }),

  makeFlashcard(sources, {
    id: "da-union-005",
    pathId,
    topic: "Art. 58–60 UMV — Verfall und Nichtigkeit",
    title: "Verfall (Art. 58) vs. Nichtigkeit (Art. 59/60 UMV): Zeitliche Wirkung und Gründe",
    prompt:
      "Was unterscheidet Verfall (Art. 58 UMV) von absoluter Nichtigkeit (Art. 59 UMV) und relativer Nichtigkeit (Art. 60 UMV) hinsichtlich zeitlicher Wirkung, Gründen und Antragsbefugnis?",
    explanation:
      "Verfall (Art. 58 UMV): Tritt ex nunc ein — die Marke verliert ihren Schutz erst ab dem Feststellungszeitpunkt. Gründe: Art. 58 Abs. 1 lit. a — fünf Jahre Nichtbenutzung (Art. 18); lit. b — die Marke ist zu einer gebräuchlichen Gattungsbezeichnung geworden; lit. c — die Marke ist irreführend geworden. Antragsbefugnis: jede natürliche oder juristische Person (Art. 63 UMV). Absolute Nichtigkeit (Art. 59 UMV): Marke hätte nie eingetragen werden dürfen — ex tunc. Gründe: Verstoß gegen Art. 7 UMV oder bösgläubige Anmeldung. Relative Nichtigkeit (Art. 60 UMV): Geltendmachung älterer Rechte — ex tunc, nur durch den Inhaber des älteren Rechts. Zuständig für Nichtigkeits-/Verfallsanträge: das EUIPO (EUTM Cancellation Division).",
    sourceIds: ["umv58", "umv59", "umv60"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["unionsmarke", "verfall", "nichtigkeit", "art58-umv", "art59-umv", "art60-umv"],
    reviewStatus: "reviewed",
  }),

  makeFlashcard(sources, {
    id: "da-union-006",
    pathId,
    topic: "Art. 15 UMV — Erschöpfung",
    title: "EWR-weite Erschöpfung der Unionsmarke (Art. 15 UMV)",
    prompt:
      "Unter welchen Voraussetzungen tritt Erschöpfung des Unionsmarkenrechts nach Art. 15 UMV ein, und wie weit reicht ihre territoriale Wirkung?",
    explanation:
      "Art. 15 Abs. 1 UMV: Das durch die Unionsmarke verliehene Recht erschöpft sich hinsichtlich einer Ware, die unter dieser Marke vom Markeninhaber oder mit seiner Zustimmung in der Union in den Verkehr gebracht worden ist. Territoriale Reichweite: EWR-weit (durch Parallelvorschriften des EWR-Abkommens auf den gesamten EWR erstreckt). Keine internationale Erschöpfung: Inverkehrbringen außerhalb der EU/des EWR durch den Markeninhaber erschöpft das Markenrecht nicht — Einfuhr aus Drittstaaten ohne Zustimmung ist Verletzung. Ausnahme (Art. 15 Abs. 2 UMV): Der Markeninhaber kann sich der weiteren Benutzung widersetzen, wenn berechtigte Gründe bestehen — insb. Veränderung oder Verschlechterung der Waren nach dem Inverkehrbringen.",
    sourceIds: ["umv15"],
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["unionsmarke", "erschoepfung", "ewr", "art15-umv"],
    reviewStatus: "reviewed",
  }),

  makeFlashcard(sources, {
    id: "da-union-007",
    pathId,
    topic: "Art. 139 ff. UMV — Umwandlung/Konversion",
    title: "Umwandlung der Unionsmarkenanmeldung in nationale Anmeldungen",
    prompt:
      "In welchen Situationen kann eine Unionsmarkenanmeldung oder -eintragung in nationale Anmeldungen umgewandelt werden, und welche Konsequenz hat die Umwandlung für den Zeitrang?",
    explanation:
      "Art. 139 UMV: Wird eine Unionsmarkenanmeldung zurückgewiesen oder eine eingetragene Unionsmarke für verfallen oder nichtig erklärt, kann der Anmelder bzw. Markeninhaber beantragen, dass die Anmeldung oder Eintragung in eine nationale Markenanmeldung umgewandelt wird. Voraussetzungen (Art. 139 Abs. 2): Die Umwandlung ist ausgeschlossen, wenn die Zurückweisung auf absoluten Hindernissen beruht, die in dem betreffenden Mitgliedstaat ebenfalls greifen würden, oder wenn die nationale Marke in dem Mitgliedstaat nicht eingetragen werden könnte. Zeitrang: Die nationale Anmeldung, die aus der Umwandlung hervorgeht, genießt denselben Anmeldetag und ggf. dieselbe Priorität wie die ursprüngliche Unionsmarkenanmeldung (Art. 141 Abs. 1 UMV).",
    sourceIds: ["umv139"],
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["unionsmarke", "umwandlung", "konversion", "art139-umv"],
    reviewStatus: "reviewed",
  }),

  // ─── MC SINGLE / MULTI da-union-008 … da-union-034 ───────────────────────

  makeSingle(sources, {
    id: "da-union-008",
    pathId,
    topic: "Art. 4 UMV — Markenfähige Zeichen (Fallkonstellation)",
    title: "Fall: Eintragungsfähigkeit einer Klangmarke nach 2017",
    prompt:
      "Unternehmen U möchte ein charakteristisches Motorengeräusch seines Luxusautos als Unionsmarke für Kraftfahrzeuge eintragen. Die Klangsequenz ist als MP3-Audiodatei beim EUIPO eingereicht. Das EUIPO prüft, ob Art. 4 UMV erfüllt ist. Welche Anforderung ist nach geltendem Recht (seit 2017) entscheidend?",
    options: [
      "Das Zeichen muss grafisch darstellbar sein — eine Notenschrift oder Sonagramm-Darstellung ist zwingend.",
      "Das Zeichen muss im Register so dargestellt werden, dass der Schutzgegenstand klar und eindeutig bestimmbar ist; eine Audiodatei kann genügen.",
      "Klänge sind nach Art. 4 UMV generell von der Eintragung ausgeschlossen.",
      "Die Markenfähigkeit eines Klangs setzt voraus, dass er vom Verkehr bereits als Marke erkannt wird (Verkehrsdurchsetzung).",
    ],
    answer: 1,
    explanation:
      "Art. 4 UMV (Fassung 2017): Das frühere Erfordernis der grafischen Darstellbarkeit wurde abgeschafft. Maßgeblich ist jetzt, ob das Zeichen im Register klar und eindeutig bestimmbar dargestellt werden kann. Eine MP3-Audiodatei, die das Klangzeichen präzise und reproduzierbar wiedergibt, erfüllt diese Anforderung (die zulässigen Darstellungsformen je Markentyp konkretisiert die EUIPO-Durchführungsverordnung (EU) 2018/626). Die grafische Darstellung (Notenschrift, Sonagramm) ist also keine zwingende Voraussetzung mehr. Verkehrsdurchsetzung ist eine eigenständige Frage (Art. 7 Abs. 3 UMV), die erst relevant wird, wenn ein Schutzhindernis vorliegt.",
    sourceIds: ["umv4"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["unionsmarke", "markenfaehigkeit", "klangmarke", "art4-umv"],
    // FIX (Orchestrator): DVO-Zitat auf geltende EUIPO-DVO (EU) 2018/626 aktualisiert (ersetzte 2017/1431); kein unsicheres Sub-Artikel-Zitat mehr. Kern (answer 1) korrekt.
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-009",
    pathId,
    topic: "Art. 7 Abs. 1 lit. b UMV — Fehlende Unterscheidungskraft",
    title: "Absolutes Schutzhindernis: fehlende Unterscheidungskraft (Art. 7 Abs. 1 lit. b UMV)",
    prompt:
      "Welches der folgenden Zeichen erfüllt am ehesten das Schutzhindernis der fehlenden Unterscheidungskraft nach Art. 7 Abs. 1 lit. b UMV?",
    options: [
      "Das Fantasiewort 'VOREX' für pharmazeutische Erzeugnisse",
      "Die Farbkombination Dunkelblau/Grün in einem abstrakten Muster für Bekleidung",
      "Das Wort 'BEST' für Konsumgüter aller Art",
      "Eine stilisierte Löwenfigur als Bildmarke für Finanzdienstleistungen",
    ],
    answer: 2,
    explanation:
      "Art. 7 Abs. 1 lit. b UMV: Nicht eintragungsfähig sind Marken, die keine Unterscheidungskraft haben. 'BEST' ist eine rein lobende Angabe allgemeinen Charakters für Waren jedweder Art — der Verkehr versteht das Zeichen als Qualitätsanpreisung, nicht als Hinweis auf die betriebliche Herkunft. Solchen selbstlobenden Werbeaussagen fehlt typischerweise jede Unterscheidungskraft (st. Rspr. des EuG zu rein anpreisenden Angaben, vgl. 'BEST BUY'). Fantasieworte ('VOREX'), abstrakte Muster mit charakteristischen Farbkombinationen und stilisierte Figuren können dagegen Unterscheidungskraft aufweisen.",
    sourceIds: ["umv7"],
    levelNumber: 1,
    errorCategory: "Subsumtion",
    skillTags: ["unionsmarke", "schutzhindernisse", "unterscheidungskraft", "art7-umv"],
    // FIX (Orchestrator): unsicheres Aktenzeichen T-135/04 entfernt, Prinzip + Fallname 'BEST BUY' beibehalten. Kern (answer 2) korrekt.
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-010",
    pathId,
    topic: "Art. 7 Abs. 1 lit. e UMV — Formmarke (Fallkonstellation)",
    title: "Fall: technisch bedingte Form — Überwindbarkeit durch Verkehrsdurchsetzung?",
    prompt:
      "Hersteller H produziert einen Ventilator mit einer besonderen Schaufelanordnung, die für eine optimale Luftströmung technisch notwendig ist. H möchte die dreidimensionale Form als Unionsmarke eingetragen haben. Das EUIPO verweigert dies. H beruft sich auf Verkehrsdurchsetzung (Art. 7 Abs. 3 UMV). Hat dieses Argument Erfolg?",
    options: [
      "Ja — Art. 7 Abs. 3 UMV lässt Verkehrsdurchsetzung ausnahmslos für alle absoluten Hindernisse zu.",
      "Nein — das Hindernis aus Art. 7 Abs. 1 lit. e (ii) UMV (technisch bedingte Form) ist absolut und wird von Art. 7 Abs. 3 UMV ausdrücklich nicht erfasst.",
      "Ja, aber nur wenn die Verkehrsdurchsetzung in allen EU-Mitgliedstaaten nachgewiesen wird.",
      "Nein — dreidimensionale Marken können grundsätzlich nicht als Unionsmarke eingetragen werden.",
    ],
    answer: 1,
    explanation:
      "Art. 7 Abs. 1 lit. e (ii) UMV schließt Zeichen aus, die ausschließlich aus der Form der Ware bestehen, die zur Erreichung einer technischen Wirkung erforderlich ist (EuGH 'Philips', C-299/99). Art. 7 Abs. 3 UMV ermöglicht Verkehrsdurchsetzung ausschließlich für die Hindernisse aus lit. b, c und d — nicht für lit. e. Der Ausschluss technisch bedingter Formen ist daher absolut und unüberwindbar, weil eine Monopolisierung solcher Formen den freien Wettbewerb dauerhaft beeinträchtigen würde.",
    sourceIds: ["umv7"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["unionsmarke", "schutzhindernisse", "formmarke", "verkehrsdurchsetzung", "art7-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-011",
    pathId,
    topic: "Art. 7 Abs. 3 UMV — Verkehrsdurchsetzung",
    title: "Verkehrsdurchsetzung bei Unionsmarke: Maßstab und Territorialität",
    prompt:
      "Für welche absoluten Hindernisse des Art. 7 Abs. 1 UMV kann Verkehrsdurchsetzung nach Art. 7 Abs. 3 UMV geltend gemacht werden, und in welchem Gebiet muss die Durchsetzung nachgewiesen werden?",
    options: [
      "Für alle absoluten Hindernisse des Art. 7 Abs. 1 UMV; Nachweis im Inland eines Mitgliedstaats genügt.",
      "Nur für lit. b (fehlende Unterscheidungskraft), lit. c (beschreibend) und lit. d (gattungsüblich); Nachweis in dem Teil der Union, in dem das Hindernis bestand.",
      "Nur für lit. b und lit. c; lit. d (Gattungsbezeichnung) ist nicht heilbar.",
      "Für lit. b, c, d und lit. e; Nachweis in der gesamten Union erforderlich.",
    ],
    answer: 1,
    explanation:
      "Art. 7 Abs. 3 UMV: Die Hindernisse nach lit. b, c und d stehen der Eintragung nicht entgegen, wenn die Marke infolge ihrer Benutzung Unterscheidungskraft erworben hat. Der Nachweis der Verkehrsdurchsetzung muss sich auf den Teil der Union erstrecken, in dem das Schutzhindernis bestand (EuGH 'Windsurfing Chiemsee', C-108/97). Bei einem unionsweiten Hindernis (z.B. in mehreren Mitgliedstaaten beschreibend) ist entsprechend breiter Nachweis erforderlich. Lit. e (Formausschlüsse) ist ausdrücklich nicht in Art. 7 Abs. 3 UMV enthalten und damit nicht heilbar.",
    sourceIds: ["umv7"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["unionsmarke", "verkehrsdurchsetzung", "art7-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-012",
    pathId,
    topic: "Art. 7 Abs. 1 lit. e (iii) UMV — Wertform (Fallkonstellation)",
    title: "Fall: Form verleiht wesentlichen Wert — Eintragungshindernis",
    prompt:
      "Designerin D hat für ihr Schmuckstück eine außergewöhnlich ästhetische dreidimensionale Form kreiert, die Käufer gerade wegen ihrer Schönheit kaufen. D möchte diese Form als Unionsmarke schützen. Das EUIPO weist zurück. Welches Hindernis greift, und kann D es überwinden?",
    options: [
      "Art. 7 Abs. 1 lit. b UMV (fehlende Unterscheidungskraft); heilbar durch Verkehrsdurchsetzung.",
      "Art. 7 Abs. 1 lit. e (iii) UMV (Form verleiht wesentlichen Wert); nicht heilbar durch Verkehrsdurchsetzung.",
      "Art. 7 Abs. 1 lit. c UMV (beschreibendes Zeichen); heilbar durch Verkehrsdurchsetzung.",
      "Art. 7 Abs. 1 lit. f UMV (sittenwidrig); nicht heilbar, aber Designschutz wäre möglich.",
    ],
    answer: 1,
    explanation:
      "Art. 7 Abs. 1 lit. e (iii) UMV: Von der Eintragung ausgeschlossen sind Zeichen, die ausschließlich aus der Form oder einem anderen charakteristischen Merkmal der Ware bestehen, durch die der Ware ein wesentlicher Wert verliehen wird. Die Ästhetik als Hauptkaufmotiv zeigt, dass die Form den wesentlichen (ökonomischen) Wert der Ware begründet (EuGH 'Gömböc', C-237/19). Da dieses Hindernis in Art. 7 Abs. 3 UMV nicht aufgeführt ist, kann es nicht durch Verkehrsdurchsetzung überwunden werden. Alternativ kann Designschutz (Gemeinschaftsgeschmacksmuster) erwogen werden.",
    sourceIds: ["umv7"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["unionsmarke", "schutzhindernisse", "formmarke", "wertform", "art7-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-013",
    pathId,
    topic: "Art. 8 Abs. 1 lit. b UMV — Verwechslungsgefahr (Fallkonstellation)",
    title: "Fall: Verwechslungsgefahr — Kumulativität von Zeichen- und Warenähnlichkeit",
    prompt:
      "Die ältere Unionsmarke 'LUXORA' ist für Parfüm und Kosmetika eingetragen. Der Anmelder der jüngeren Marke 'LUXORA' möchte diese für Bürostühle eintragen. Der Inhaber der älteren Marke erhebt Widerspruch wegen Verwechslungsgefahr (Art. 8 Abs. 1 lit. b UMV). Wie ist der Widerspruch zu beurteilen?",
    options: [
      "Der Widerspruch hat Erfolg — bei identischen Zeichen reicht allein die Zeichenidentität für Verwechslungsgefahr.",
      "Der Widerspruch scheitert — Art. 8 Abs. 1 lit. b UMV verlangt kumulativ Zeichen- UND Warenähnlichkeit; zwischen Parfüm und Bürostühlen besteht keine Warenähnlichkeit.",
      "Der Widerspruch hat Erfolg, wenn die ältere Marke in der Union bekannt ist (Art. 8 Abs. 5 UMV).",
      "Der Widerspruch scheitert, weil Verwechslungsgefahr nur bei identischen Waren geprüft wird.",
    ],
    answer: 1,
    explanation:
      "Art. 8 Abs. 1 lit. b UMV setzt kumulativ voraus: Ähnlichkeit/Identität der Zeichen UND Ähnlichkeit/Identität der Waren oder Dienstleistungen. Fehlt eine der Voraussetzungen, scheidet Verwechslungsgefahr nach lit. b aus. Parfüm und Bürostühle sind gänzlich unähnlige Waren (verschiedene Art, Zweck, Verwendung, Vertriebskanäle). Damit ist der Widerspruch nach Art. 8 Abs. 1 lit. b UMV unbegründet. Der Hinweis auf Art. 8 Abs. 5 UMV (Bekanntheitsschutz) wäre ein anderer Streitgegenstand, auf den der Widersprechende sich ggf. zusätzlich stützen könnte.",
    sourceIds: ["umv8"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["unionsmarke", "verwechslungsgefahr", "warenaehnlichkeit", "art8-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-014",
    pathId,
    topic: "Art. 8 Abs. 5 UMV — Schutz bekannter Marken",
    title: "Bekanntheitsschutz (Art. 8 Abs. 5 UMV): Voraussetzungen",
    prompt:
      "Welche Voraussetzungen muss ein Widerspruch auf Art. 8 Abs. 5 UMV (Schutz bekannter Marken) erfüllen?",
    options: [
      "Zeichenähnlichkeit + Warenähnlichkeit + Bekanntheit der älteren Marke + keine Verwechslungsgefahr nötig.",
      "Bekanntheit der älteren Marke bei einem erheblichen Teil des Publikums der Union; Zeichenähnlichkeit oder -identität; die Nutzung der jüngeren Marke würde die Unterscheidungskraft oder Wertschätzung der älteren Marke ohne rechtfertigenden Grund in unlauterer Weise ausnutzen oder beeinträchtigen — Warenähnlichkeit ist nicht erforderlich.",
      "Identität der Zeichen + Bekanntheit in der gesamten Union + nachgewiesener Schaden.",
      "Warenähnlichkeit + Bekanntheit in mindestens drei EU-Mitgliedstaaten.",
    ],
    answer: 1,
    explanation:
      "Art. 8 Abs. 5 UMV: Auf Widerspruch des Inhabers einer bekannten älteren Marke wird die Eintragung der angemeldeten Marke versagt, wenn sie mit der bekannten Marke identisch oder dieser ähnlich ist — und zwar auch dann, wenn sie für andere Waren oder Dienstleistungen angemeldet wird. Weitere Voraussetzung: Die Benutzung der jüngeren Marke würde die Unterscheidungskraft oder die Wertschätzung der älteren Marke ohne rechtfertigenden Grund in unlauterer Weise ausnutzen oder beeinträchtigen (Verwässerung, Rufschädigung, Trittbrettfahren). Bekanntheit genügt in einem erheblichen Teil der Union; keine Warenähnlichkeit erforderlich.",
    sourceIds: ["umv8"],
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["unionsmarke", "bekanntheitsschutz", "art8-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-015",
    pathId,
    topic: "Art. 8 Abs. 5 UMV — Bekannte Marke (Fallkonstellation)",
    title: "Fall: Bekannte Marke — Trittbrettfahren ohne Warenähnlichkeit",
    prompt:
      "Die Unionsmarke 'STARLIGHT' ist als bekannte Marke für Luxusuhren eingetragen und genießt hohe Wertschätzung. Ein Getränkehersteller meldet 'STARLIGHT' für alkoholfreie Erfrischungsgetränke an. Der Uhreninhaber legt Widerspruch nach Art. 8 Abs. 5 UMV ein. Begründet der Widerspruch Erfolgsaussichten?",
    options: [
      "Nein — zwischen Uhren und Erfrischungsgetränken besteht keine Warenähnlichkeit; Art. 8 Abs. 1 lit. b UMV greift nicht.",
      "Ja — nach Art. 8 Abs. 5 UMV ist Warenähnlichkeit nicht erforderlich; entscheidend ist, ob die Nutzung die Wertschätzung der bekannten Marke in unlauterer Weise ausnutzen würde.",
      "Nein — Art. 8 Abs. 5 UMV gilt nur für identische Zeichen, nicht für ähnliche.",
      "Ja — bekannte Marken genießen absoluten Schutz gegen alle Neuanmeldungen.",
    ],
    answer: 1,
    explanation:
      "Art. 8 Abs. 5 UMV schützt bekannte Marken auch gegenüber Anmeldungen für nicht ähnliche Waren oder Dienstleistungen. Voraussetzung ist, dass die Benutzung der angemeldeten Marke die Unterscheidungskraft oder Wertschätzung der bekannten Marke ohne rechtfertigenden Grund in unlauterer Weise ausnutzen (Trittbrettfahren) oder beeinträchtigen würde. Ein Getränkehersteller, der den guten Ruf einer bekannten Luxusuhrenmarke auf seine Produkte transferiert, nutzt die Wertschätzung in unlauterer Weise aus. Der Widerspruch hat damit gute Erfolgsaussichten.",
    sourceIds: ["umv8"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["unionsmarke", "bekanntheitsschutz", "trittbrettfahren", "art8-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-016",
    pathId,
    topic: "Art. 46 UMV — Widerspruchsfrist",
    title: "Widerspruchsfrist gegen eine Unionsmarkenanmeldung (Art. 46 UMV)",
    prompt:
      "Innerhalb welcher Frist kann nach Art. 46 UMV Widerspruch gegen eine veröffentlichte Unionsmarkenanmeldung beim EUIPO eingelegt werden?",
    options: [
      "6 Monate ab Veröffentlichung der Anmeldung",
      "3 Monate ab Veröffentlichung der Anmeldung",
      "2 Monate ab Bekanntgabe der Eintragung",
      "1 Jahr ab Anmeldetag",
    ],
    answer: 1,
    explanation:
      "Art. 46 Abs. 1 UMV: Innerhalb einer Frist von drei Monaten nach Veröffentlichung der Unionsmarkenanmeldung im Blatt für Unionsmarken kann Widerspruch gegen die Eintragung erhoben werden. Diese Frist ist nicht verlängerbar. Der Widerspruch kann auf relative Eintragungshindernisse nach Art. 8 UMV gestützt werden (ältere Marken, sonstige ältere Rechte). Das Widerspruchsverfahren findet beim EUIPO statt; ein Widerspruch vor nationalen Gerichten ist nicht vorgesehen.",
    sourceIds: ["umv46"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["unionsmarke", "widerspruchsverfahren", "euipo", "art46-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-017",
    pathId,
    topic: "Art. 30–42 UMV — Anmeldeverfahren EUIPO (Fallkonstellation)",
    title: "Fall: EUIPO-Amtsprüfung — nur absolute, keine relativen Hindernisse",
    prompt:
      "Anmelderin A reicht beim EUIPO eine Unionsmarkenanmeldung für Softwareprodukte ein. Das EUIPO prüft die Anmeldung. Die ältere Unionsmarke 'SOFTLAB' für ähnliche Software wird vom EUIPO in der Amtsprüfung nicht berücksichtigt. Warum ist das korrekt?",
    options: [
      "Das EUIPO prüft grundsätzlich alle Hindernisse, hat aber den Widerspruch der Inhaberin von 'SOFTLAB' abgewartet.",
      "Das EUIPO prüft im Amtsverfahren ausschließlich absolute Hindernisse (Art. 7 UMV) von Amts wegen; relative Hindernisse (Art. 8 UMV) werden nur auf Widerspruch hin geprüft.",
      "Das EUIPO prüft nur dann relative Hindernisse, wenn die ältere Marke im Recherchenbericht aufgeführt ist.",
      "Relative Hindernisse werden in der EU erst nach Eintragung in einem Nichtigkeitsverfahren geprüft.",
    ],
    answer: 1,
    explanation:
      "Das EUIPO-Anmeldeverfahren (Art. 41 f. UMV) ist so strukturiert: Amtsprüfung umfasst ausschließlich absolute Eintragungshindernisse nach Art. 7 UMV — das EUIPO prüft von Amts wegen, ob die Anmeldung schutzfähig ist. Relative Eintragungshindernisse (Art. 8 UMV — ältere Marken, sonstige Rechte) werden dagegen nicht von Amts wegen geprüft. Sie können nur durch Widerspruch des Inhabers des älteren Rechts innerhalb der Widerspruchsfrist (Art. 46 UMV: 3 Monate nach Veröffentlichung) geltend gemacht werden. Dies ist ein grundlegender Unterschied zu manchen nationalen Systemen.",
    sourceIds: ["umv7", "umv46"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["unionsmarke", "anmeldeverfahren", "euipo", "amtspruefung", "art7-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-018",
    pathId,
    topic: "Art. 18 UMV — Benutzungspflicht: Fristbeginn",
    title: "Benutzungspflicht — Fristbeginn ab Eintragung, nicht ab Anmeldung",
    prompt:
      "Unionsmarke 'CRONEX' wurde am 10. Januar 2019 angemeldet und am 15. März 2020 eingetragen. Ab wann beginnt die fünfjährige Benutzungsfrist nach Art. 18 UMV zu laufen?",
    options: [
      "Ab dem 10. Januar 2019 (Anmeldetag)",
      "Ab dem 15. März 2020 (Eintragungsdatum)",
      "Ab dem Zeitpunkt der Veröffentlichung der Anmeldung",
      "Ab dem Zeitpunkt der ersten Benutzung im Handel",
    ],
    answer: 1,
    explanation:
      "Art. 18 Abs. 1 UMV: Die fünfjährige Benutzungsfrist beginnt ab dem Zeitpunkt der Eintragung der Unionsmarke zu laufen — nicht ab dem Anmeldetag. Im vorliegenden Fall läuft die Benutzungspflicht also ab dem 15. März 2020; die Frist endet am 15. März 2025. Vor Ablauf dieser Frist kann niemand mit Erfolg Verfall wegen Nichtbenutzung geltend machen (Benutzungsschonfrist). Diese Regelung unterscheidet sich von manchen nationalen Systemen und ist klausurrelevant, weil Anmelde- und Eintragungsdatum oft weit auseinander liegen.",
    sourceIds: ["umv18"],
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["unionsmarke", "benutzungspflicht", "fristbeginn", "art18-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-019",
    pathId,
    topic: "Art. 58 Abs. 1 lit. a UMV — Verfall wegen Nichtbenutzung (Fallkonstellation)",
    title: "Fall: Verfallsantrag wegen Nichtbenutzung — Rechtsfolge ex nunc",
    prompt:
      "Die Unionsmarke 'VERALTO' wurde am 1. Juni 2016 eingetragen. Der Inhaber hat die Marke seit 2018 nicht mehr benutzt. Konkurrent K stellt im Juli 2024 einen Verfallsantrag beim EUIPO. Das EUIPO gibt dem Antrag statt. Ab wann verliert die Marke ihren Schutz?",
    options: [
      "Ab dem 1. Juni 2016 (Eintragungsdatum) — der Verfall wirkt ex tunc.",
      "Ab dem Zeitpunkt, ab dem die Nichtbenutzung begann (2018) — ex tunc ab Benutzungsende.",
      "Ab dem Zeitpunkt der Feststellung des Verfalls durch das EUIPO (ex nunc).",
      "Erst mit Ablauf einer dreimonatigen Übergangsfrist nach der EUIPO-Entscheidung.",
    ],
    answer: 2,
    explanation:
      "Art. 58 Abs. 1 lit. a UMV: Verfall tritt ein, wenn die Marke innerhalb eines ununterbrochenen Zeitraums von fünf Jahren nicht ernsthaft in der Union benutzt wurde. Rechtsfolge nach Art. 62 Abs. 1 UMV: Der Verfall wirkt ex nunc — die Marke verliert ihren Schutz ab dem Zeitpunkt der Feststellung (Entscheidung des EUIPO oder des Unionsmarkengerichts). Sie gilt nicht rückwirkend als nie eingetragen (das wäre ex tunc, was der Nichtigkeit vorbehalten ist). Praxisrelevanz: Verletzungshandlungen vor dem Verfallszeitpunkt können noch Ansprüche begründen.",
    sourceIds: ["umv58"],
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["unionsmarke", "verfall", "ex-nunc", "art58-umv"],
    reviewStatus: "reviewed",
  }),

  // ─── ORDERING da-union-020 ────────────────────────────────────────────────

  makeOrdering(sources, {
    id: "da-union-020",
    pathId,
    topic: "Art. 8 Abs. 1 lit. b UMV — Prüfungsschema Verwechslungsgefahr",
    title: "Prüfungsschema: Verwechslungsgefahr (Art. 8 Abs. 1 lit. b UMV) — korrekte Reihenfolge",
    prompt:
      "Ordnen Sie die Prüfungsschritte der Verwechslungsgefahr nach Art. 8 Abs. 1 lit. b UMV in die gutachtlich korrekte Reihenfolge.",
    orderItems: [
      "1. Waren-/Dienstleistungsähnlichkeit: Vergleich der von den kollidierenden Marken erfassten Waren/DL (Art, Zweck, Nutzung, Vertriebskanäle, Wettbewerbs- oder Komplementärverhältnis)",
      "2. Zeichenähnlichkeit: visueller, klanglicher und begrifflicher Vergleich der Zeichen; Gesamteindruck aus Sicht des maßgeblichen Durchschnittsverbrauchers",
      "3. Kennzeichnungskraft der älteren Marke: originäre Kennzeichnungskraft (schwach / normal / stark) und ggf. durch Benutzung erworbene gesteigerte Kennzeichnungskraft",
      "4. Gesamtabwägung nach dem Wechselwirkungsprinzip (EuGH 'Canon'): alle Faktoren in ihrer Wechselbeziehung; Ergebnis aus Sicht des aufmerksamen Durchschnittsverbrauchers der betreffenden Waren/DL",
    ],
    explanation:
      "Das von EuGH und EuG (zu Art. 8 Abs. 1 lit. b UMV) entwickelte Prüfungsschema: (1) Waren-/DL-Ähnlichkeit — ohne Ähnlichkeit der Waren/DL scheidet Verwechslungsgefahr nach lit. b grundsätzlich aus (EuGH 'Canon', C-39/97). (2) Zeichenähnlichkeit — Gesamteindrucksgrundsatz; prägender Bestandteil bestimmt ggf. den Vergleich. (3) Kennzeichnungskraft der älteren Marke — je stärker die Kennzeichnungskraft, desto weiter der Schutzbereich. (4) Gesamtabwägung: Wechselwirkung aller Faktoren; ein geringer Grad der einen Komponente kann durch höheren Grad einer anderen ausgeglichen werden. Ergebnis: Bejahen oder Verneinen der Verwechslungsgefahr.",
    sourceIds: ["umv8"],
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["unionsmarke", "verwechslungsgefahr", "pruefungsschema", "wechselwirkung", "art8-umv"],
    reviewStatus: "reviewed",
  }),

  // ─── MC weiter da-union-021 … da-union-034 ───────────────────────────────

  makeSingle(sources, {
    id: "da-union-021",
    pathId,
    topic: "Art. 9 UMV — Verletzungstatbestände",
    title: "Verletzungstatbestände der Unionsmarke (Art. 9 UMV): Überblick",
    prompt:
      "Welcher der folgenden Verletzungstatbestände des Art. 9 Abs. 2 UMV setzt ausdrücklich keine Verwechslungsgefahr voraus?",
    options: [
      "Art. 9 Abs. 2 lit. b UMV — Benutzung eines ähnlichen Zeichens für ähnliche Waren/DL mit Verwechslungsgefahr",
      "Art. 9 Abs. 2 lit. a UMV — Doppelidentität (identisches Zeichen, identische Waren/DL)",
      "Art. 9 Abs. 2 lit. c UMV — Benutzung eines identischen oder ähnlichen Zeichens für nicht ähnliche Waren/DL bei bekannter Marke",
      "Art. 9 Abs. 2 lit. a UMV und lit. c UMV setzen beide keine Verwechslungsgefahr voraus.",
    ],
    answer: 3,
    explanation:
      "Art. 9 Abs. 2 UMV: lit. a (Doppelidentität) setzt keine Verwechslungsgefahr voraus — sie wird unwiderleglich vermutet. lit. b verlangt dagegen positiv Verwechslungsgefahr (Zeichenähnlichkeit + Warenähnlichkeit + Verwechslungsgefahr). lit. c (Bekanntheitsschutz) schützt gegen Ausnutzung/Beeinträchtigung der Wertschätzung ohne Verwechslungsgefahr. Damit setzen sowohl lit. a als auch lit. c keine Verwechslungsgefahr im eigentlichen Sinne voraus — Antwort D ist korrekt.",
    sourceIds: ["umv9"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["unionsmarke", "verletzung", "doppelidentitaet", "art9-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-022",
    pathId,
    topic: "Art. 124–130 UMV — Unionsmarkengerichte (Fallkonstellation)",
    title: "Fall: Zuständigkeit des Unionsmarkengerichts für Verletzungsklage",
    prompt:
      "Markeninhaber M mit Sitz in Frankreich klagt wegen Verletzung seiner Unionsmarke gegen Verletzer V mit Sitz in Deutschland. V hat die Verletzung in Deutschland, Österreich und Polen begangen. Welches Gericht ist für eine unionsweite Verletzungsklage zuständig?",
    options: [
      "Das Handelsgericht Paris, weil M seinen Sitz in Frankreich hat.",
      "Ein deutsches Unionsmarkengericht (Art. 125 Abs. 1 UMV), das als Unionsmarkengericht erster Instanz für den Sitz des Beklagten V zuständig ist; seine Entscheidung wirkt für die gesamte Union.",
      "Jedes nationale Gericht in Deutschland, Österreich und Polen, jeweils für die in dem Staat begangenen Verletzungen.",
      "Ausschließlich das EUIPO — Verletzungsklagen werden nicht von nationalen Gerichten entschieden.",
    ],
    answer: 1,
    explanation:
      "Art. 124 UMV: Die Mitgliedstaaten benennen nationale Gerichte als Unionsmarkengerichte. Art. 125 Abs. 1 UMV: Für Verletzungsklagen ist das Unionsmarkengericht am Sitz des Beklagten zuständig (hier: deutsches Gericht). Art. 126 UMV: Das so zuständige Gericht entscheidet über Verletzungen, die im gesamten Gebiet der Union begangen werden oder drohen — seine Entscheidung hat unionweite Wirkung. Das EUIPO ist nicht für Verletzungsklagen zuständig; es ist eine Verwaltungsbehörde für Eintragung und Nichtigkeits-/Verfallsverfahren.",
    sourceIds: ["umv124", "umv125"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["unionsmarke", "unionsmarkengericht", "zustaendigkeit", "art124-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-023",
    pathId,
    topic: "Art. 130 UMV — Einstweiliger Rechtsschutz",
    title: "Einstweiliger Rechtsschutz durch Unionsmarkengerichte (Art. 130 UMV)",
    prompt:
      "Was ist die Besonderheit des einstweiligen Rechtsschutzes, den ein Unionsmarkengericht nach Art. 130 UMV gewähren kann, gegenüber einer Verletzungsklage nach Art. 126 UMV?",
    options: [
      "Einstweiliger Rechtsschutz ist auf das Territorium des angerufenen Mitgliedstaats beschränkt, auch wenn das Gericht als Unionsmarkengericht tätig wird.",
      "Einstweiliger Rechtsschutz hat dieselbe unionweite Wirkung wie eine Verletzungsklage nach Art. 126 UMV.",
      "Einstweiliger Rechtsschutz kann nur beim EUIPO, nicht bei nationalen Gerichten beantragt werden.",
      "Einstweiliger Rechtsschutz steht dem Markeninhaber gegen Verletzungen nur im Inland zur Verfügung.",
    ],
    answer: 0,
    explanation:
      "Art. 130 Abs. 2 UMV: Im Unterschied zur Verletzungsklage (die bei dem nach Art. 125 UMV zuständigen Gericht erhoben wird und unionweite Wirkung hat) sind einstweilige Maßnahmen, die ein nach Art. 130 Abs. 2 UMV angerufenes Gericht eines anderen Mitgliedstaats trifft, auf das Hoheitsgebiet dieses Staates beschränkt. Das Gericht, das nach Art. 125 UMV für die Hauptsacheklage zuständig ist, kann dagegen auch im Wege des einstweiligen Rechtsschutzes unionweit wirksame Maßnahmen anordnen (Art. 130 Abs. 1 UMV). Diese Unterscheidung ist prüfungsrelevant.",
    sourceIds: ["umv130"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["unionsmarke", "einstweiliger-rechtsschutz", "art130-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-024",
    pathId,
    topic: "Art. 15 UMV — Erschöpfung (Fallkonstellation)",
    title: "Fall: Parallelimport aus den USA — keine internationale Erschöpfung",
    prompt:
      "Händler H importiert Originalware des Markeninhabers M aus den USA und verkauft sie in Deutschland. M hat die Waren in den USA mit seiner Zustimmung in Verkehr gebracht, aber nicht in der EU/im EWR. Kann M den Weiterverkauf in Deutschland untersagen?",
    options: [
      "Nein — die Erschöpfung tritt weltweit ein, sobald der Markeninhaber die Ware irgendwo in Verkehr bringt.",
      "Ja — Art. 15 UMV begründet nur EWR-weite Erschöpfung; da M die Ware nicht im EWR in Verkehr gebracht hat, ist sein Markenrecht nicht erschöpft.",
      "Nein — weil es sich um Originalware handelt, liegt keine Markenverletzung vor.",
      "Ja, aber nur wenn M nachweist, dass die Waren beim Transport beschädigt wurden.",
    ],
    answer: 1,
    explanation:
      "Art. 15 Abs. 1 UMV: Erschöpfung tritt nur ein, wenn die Waren vom Markeninhaber oder mit seiner Zustimmung in der Union in den Verkehr gebracht wurden. Die Union kennt keine internationale Erschöpfung. Inverkehrbringen außerhalb des EWR (hier: USA) erschöpft das Markenrecht nicht. M kann daher den Import und Weiterverkauf in Deutschland als Verletzung seiner Unionsmarke untersagen — die Echtheit der Ware ist irrelevant für die Erschöpfungsfrage. Dies entspricht der ständigen EuGH-Rechtsprechung ('Silhouette', C-355/96).",
    sourceIds: ["umv15"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["unionsmarke", "erschoepfung", "parallelimport", "art15-umv"],
    reviewStatus: "reviewed",
  }),

  makeMulti(sources, {
    id: "da-union-025",
    pathId,
    topic: "Art. 59 UMV — Absolute Nichtigkeit",
    title: "Absolute Nichtigkeit der Unionsmarke — Gründe und Antragsbefugnis",
    prompt:
      "Welche der folgenden Aussagen zur absoluten Nichtigkeit nach Art. 59 UMV sind zutreffend? (Mehrere Antworten möglich)",
    options: [
      "Absolute Nichtigkeit kann von jeder natürlichen oder juristischen Person sowie zuständigen Behörden beantragt werden.",
      "Eine wegen bösgläubiger Anmeldung für nichtig erklärte Unionsmarke gilt als von Anfang an nicht eingetragen (ex tunc).",
      "Das EUIPO kann absolute Nichtigkeit von Amts wegen ohne Antrag feststellen.",
      "Absolute Nichtigkeit wirkt nur ex nunc — die Marke verliert ihren Schutz erst ab dem Feststellungszeitpunkt.",
    ],
    answers: [0, 1],
    explanation:
      "Zutreffend sind A und B: (A) Art. 63 Abs. 1 UMV: Anträge auf absolute Nichtigkeit (Art. 59 UMV) können von jeder natürlichen oder juristischen Person sowie von zuständigen Behörden gestellt werden. (B) Art. 62 Abs. 2 UMV: Wird die Unionsmarke für nichtig erklärt, gilt sie als von Anfang an nicht eingetragen — ex tunc-Wirkung. Nicht zutreffend: (C) Das EUIPO prüft relative und absolute Nichtigkeit nur auf Antrag — nicht von Amts wegen (anders als im Anmeldeverfahren bei absoluten Hindernissen). (D) Falsch — Nichtigkeit wirkt ex tunc (Verfall nach Art. 58 UMV wirkt ex nunc).",
    sourceIds: ["umv59"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["unionsmarke", "nichtigkeit", "ex-tunc", "art59-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-026",
    pathId,
    topic: "Art. 60 UMV — Relative Nichtigkeit (Fallkonstellation)",
    title: "Fall: Relative Nichtigkeit — Antragsbefugnis und Wirkung",
    prompt:
      "Inhaber A hat eine Unionsmarke eingetragen, die mit der älteren nationalen Marke von B für ähnliche Waren kollidiert. B versäumte die Widerspruchsfrist. Drei Jahre nach Eintragung der Unionsmarke möchte B deren Nichtigkeit beantragen. Steht B diese Möglichkeit offen?",
    options: [
      "Nein — nach Ablauf der Widerspruchsfrist ist jede Geltendmachung älterer Rechte ausgeschlossen.",
      "Ja — Art. 60 UMV erlaubt dem Inhaber älterer Rechte, einen Antrag auf relative Nichtigkeit beim EUIPO zu stellen; versäumter Widerspruch schließt diesen Weg nicht aus.",
      "Ja — aber nur wenn B nachweist, dass er die Widerspruchsfrist unverschuldet versäumt hat.",
      "Nein — relative Nichtigkeit kann nur vom Inhaber einer jüngeren Unionsmarke beantragt werden.",
    ],
    answer: 1,
    explanation:
      "Art. 60 UMV: Auf Antrag des Inhabers älterer Rechte (u.a. ältere eingetragene nationale Marke, Art. 60 Abs. 1 lit. a i.V.m. Art. 8 UMV) kann die Unionsmarke für relativ nichtig erklärt werden. Eine versäumte Widerspruchsfrist schließt den Nichtigkeitsweg nicht aus — Widerspruch und Nichtigkeitsantrag sind nebeneinander stehende Instrumente mit unterschiedlichen Fristen. Freilich gilt: Duldet B die Koexistenz über längere Zeit und nutzt er A's Marke wissentlich ohne Einwand, kann Verwirkung (Art. 61 UMV) eintreten. Im vorliegenden Fall (3 Jahre) ist der Antrag grundsätzlich noch zulässig.",
    sourceIds: ["umv60"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["unionsmarke", "relative-nichtigkeit", "art60-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-027",
    pathId,
    topic: "Art. 7 Abs. 1 lit. f UMV — Ordre public / gute Sitten",
    title: "Absolutes Schutzhindernis: Verstoß gegen die öffentliche Ordnung oder gegen die guten Sitten",
    prompt:
      "Welche Aussage zu Art. 7 Abs. 1 lit. f UMV (ordre public / gute Sitten) ist zutreffend?",
    options: [
      "Dieses Hindernis kann durch Verkehrsdurchsetzung nach Art. 7 Abs. 3 UMV überwunden werden.",
      "Dieses Hindernis ist nicht durch Verkehrsdurchsetzung heilbar; die Beurteilung richtet sich nach den Maßstäben, die in der Union allgemein anerkannt sind.",
      "Dieses Hindernis gilt nur für obszöne Zeichen, nicht für politisch brisante Bezeichnungen.",
      "Dieses Hindernis findet keine Anwendung auf Wortmarken.",
    ],
    answer: 1,
    explanation:
      "Art. 7 Abs. 1 lit. f UMV: Nicht eintragungsfähig sind Marken, die gegen die öffentliche Ordnung oder gegen die guten Sitten verstoßen. Dieses Hindernis ist in Art. 7 Abs. 3 UMV nicht aufgeführt — es ist damit nicht durch Verkehrsdurchsetzung heilbar. Die Beurteilung orientiert sich an den in der Union allgemein anerkannten Maßstäben, nicht an den Maßstäben eines einzelnen Mitgliedstaats. Das Hindernis kann Wort- und Bildmarken (z.B. anstößige Zeichen, Beleidigungen, volksverhetzende Begriffe) erfassen.",
    sourceIds: ["umv7"],
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["unionsmarke", "schutzhindernisse", "ordre-public", "art7-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-028",
    pathId,
    topic: "Art. 39/40 UMV — Seniorität (Fallkonstellation)",
    title: "Fall: Seniorität einer nationalen Marke — Koexistenz mit der Unionsmarke",
    prompt:
      "Unternehmen U besitzt eine deutsche Marke 'MAXIMA' seit 2010 für Elektromotoren. 2020 lässt es eine Unionsmarke 'MAXIMA' für dieselben Waren eintragen und beansprucht Seniorität der deutschen Marke (Art. 39/40 UMV). Was ist die Rechtsfolge der Seniorität?",
    options: [
      "Die deutsche Marke erlischt automatisch mit Beanspruchung der Seniorität; nur die Unionsmarke besteht fort.",
      "U kann die deutsche Marke aufgeben, behält aber über die Seniorität der Unionsmarke denselben Zeitrang wie die ursprüngliche deutsche Marke.",
      "Seniorität bewirkt, dass die Unionsmarke in Deutschland Vorrang vor allen anderen nationalen Marken hat.",
      "Die Seniorität gilt nur für die Laufzeit der deutschen Marke; nach deren Erlöschen entfällt sie.",
    ],
    answer: 1,
    explanation:
      "Art. 39 UMV (Seniorität aus eingetragenen nationalen Marken): Der Inhaber einer älteren nationalen Marke, der eine Unionsmarke für dasselbe Zeichen und dieselben Waren/DL anmeldet, kann die Seniorität der nationalen Marke beanspruchen. Rechtsfolge: Gibt er die nationale Marke später auf oder verlängert er sie nicht, gilt er als Inhaber der nationalen Marke mit dem ursprünglichen Zeitrang — er verliert damit keine Rechte. Der Sinn: Man kann eine nationale Marke aufgeben und nur noch die Unionsmarke unterhalten, ohne den früheren Zeitrang zu verlieren.",
    sourceIds: ["umv39"],
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["unionsmarke", "senioritaet", "koexistenz", "art39-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-029",
    pathId,
    topic: "Art. 7 Abs. 1 lit. c UMV — Beschreibende Angaben",
    title: "Beschreibende Angaben als absolutes Eintragungshindernis (Art. 7 Abs. 1 lit. c UMV)",
    prompt:
      "Welche Aussage zu Art. 7 Abs. 1 lit. c UMV (beschreibende Angaben) ist zutreffend?",
    options: [
      "Das Hindernis greift nur, wenn das Zeichen in der deutschen Sprache beschreibend ist.",
      "Das Hindernis greift bereits dann, wenn das Zeichen in einer der Amtssprachen der Union für die betreffenden Waren oder Dienstleistungen beschreibend ist — auch wenn es für die Mehrheit der Verbraucher unverständlich ist.",
      "Das Hindernis ist nach Art. 7 Abs. 2 UMV nur relevant, wenn es in der gesamten Union besteht.",
      "Beschreibende Angaben können nicht durch Verkehrsdurchsetzung eintragungsfähig werden.",
    ],
    answer: 1,
    explanation:
      "Art. 7 Abs. 2 UMV: Die absoluten Eintragungshindernisse gelten auch dann, wenn sie nur in einem Teil der Union vorliegen. Für Art. 7 Abs. 1 lit. c UMV folgt daraus: Wenn ein Zeichen auch nur in einer Amtssprache der Union für die fraglichen Waren/DL beschreibend ist, greift das Hindernis — selbst wenn es für Verbraucher in anderen Sprachräumen als Fantasiewort wahrgenommen wird (st. Rspr. zu Art. 7 Abs. 2 UMV; vgl. zur Sprachen-Frage EuGH 'MATRATZEN', C-421/04). Falsch ist Antwort D: Art. 7 Abs. 3 UMV erlaubt ausdrücklich die Verkehrsdurchsetzung auch für lit. c.",
    sourceIds: ["umv7"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["unionsmarke", "schutzhindernisse", "beschreibende-angabe", "art7-umv"],
    // FIX (Orchestrator): unsicheres Aktenzeichen "ONLINE BUS" T-104/01 ersetzt durch verifizierbare Sprachen-Leitentscheidung 'MATRATZEN' (C-421/04). Kern (answer 1) korrekt.
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-030",
    pathId,
    topic: "Art. 18 UMV — Benutzung in abgewandelter Form (Fallkonstellation)",
    title: "Fall: Benutzung in abgewandelter Form — rechtserhaltende Wirkung",
    prompt:
      "Die Unionsmarke 'DEXTRA' ist als Wortmarke eingetragen. Inhaber I benutzt sie stets in einer eigenwilligen kursiven Schrifttype mit ornamentalen Elementen. Die Kernbestandteile 'DEXTRA' sind erkennbar. Gilt dies als rechtserhaltende Benutzung nach Art. 18 Abs. 1 Unterabs. 2 UMV?",
    options: [
      "Nein — nur die exakt eingetragene Form ist rechtserhaltend; jede Abwandlung führt zu Nichtbenutzung.",
      "Ja — Art. 18 Abs. 1 Unterabs. 2 UMV lässt Benutzung in einer von der Eintragung abweichenden Form zu, sofern die Unterscheidungskraft der Marke nicht verändert wird.",
      "Ja — aber nur wenn I die abgewandelte Form ebenfalls als Unionsmarke eingetragen hat.",
      "Nein — die ornamentalen Elemente konstituieren eine eigenständige neue Marke.",
    ],
    answer: 1,
    explanation:
      "Art. 18 Abs. 1 Unterabs. 2 lit. a UMV: Als Benutzung der Unionsmarke gilt auch die Benutzung in einer Form, die von der Eintragung abweicht, sofern die Unterscheidungskraft der Marke dadurch nicht verändert wird — unabhängig davon, ob die veränderte Form ebenfalls eingetragen ist. Kursive Schrift und ornamentale Elemente, die den prägenden Wortbestandteil 'DEXTRA' unverändert lassen, berühren die Unterscheidungskraft nicht. Die Benutzung ist damit rechtserhaltend.",
    sourceIds: ["umv18"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["unionsmarke", "benutzungspflicht", "abgewandelte-form", "art18-umv"],
    reviewStatus: "reviewed",
  }),

  makeMulti(sources, {
    id: "da-union-031",
    pathId,
    topic: "Art. 30–42 UMV — Anmeldeverfahren EUIPO",
    title: "EUIPO-Anmeldeverfahren: Schritte und Besonderheiten",
    prompt:
      "Welche der folgenden Aussagen zum Anmeldeverfahren vor dem EUIPO sind zutreffend? (Mehrere Antworten möglich)",
    options: [
      "Das EUIPO erstattet dem Anmelder und den Inhabern älterer Unionsmarken einen Recherchenbericht über kollidierenden Marken.",
      "Das EUIPO prüft im Amtsverfahren absolute Eintragungshindernisse nach Art. 7 UMV von Amts wegen.",
      "Das EUIPO prüft im Amtsverfahren auch relative Eintragungshindernisse (Art. 8 UMV) von Amts wegen.",
      "Die Veröffentlichung der Unionsmarkenanmeldung löst die dreimonatige Widerspruchsfrist (Art. 46 UMV) aus.",
    ],
    answers: [0, 1, 3],
    explanation:
      "Zutreffend sind A, B und D: (A) Art. 43 UMV: Das EUIPO erstellt nach der Formalprüfung einen Recherchenbericht über frühere Unionsmarken und übermittelt ihn dem Anmelder sowie den Inhabern der recherchierten Marken. (B) Art. 41 UMV: Das EUIPO prüft absolute Hindernisse (Art. 7 UMV) von Amts wegen. (D) Art. 46 Abs. 1 UMV: Die Veröffentlichung der Anmeldung löst die dreimonatige Widerspruchsfrist aus. Nicht zutreffend: (C) Relative Hindernisse (Art. 8 UMV) werden nicht von Amts wegen geprüft — dies erfolgt nur auf Widerspruch des Rechtsinhabers.",
    sourceIds: ["umv46", "umv7"],
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["unionsmarke", "anmeldeverfahren", "euipo", "art46-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-032",
    pathId,
    topic: "Art. 139 UMV — Umwandlung (Fallkonstellation)",
    title: "Fall: Umwandlung einer abgelehnten Unionsmarkenanmeldung",
    prompt:
      "Anmelderin A meldet die Unionsmarke 'NATURIA' für Kosmetikprodukte an. Das EUIPO weist die Anmeldung zurück, weil das Zeichen in Spanien und Portugal für die betreffenden Waren beschreibend ist (Art. 7 Abs. 1 lit. c i.V.m. Abs. 2 UMV). A möchte dennoch Schutz in Deutschland, Frankreich und Österreich erhalten, wo das Zeichen nicht beschreibend ist. Was kann A tun?",
    options: [
      "A kann beim EUIPO Beschwerde einlegen, um eine auf Deutschland, Frankreich und Österreich beschränkte Unionsmarke zu erhalten.",
      "A kann gemäß Art. 139 UMV die Umwandlung der zurückgewiesenen Anmeldung in nationale Anmeldungen für Deutschland, Frankreich und Österreich beantragen.",
      "A hat keine Möglichkeit mehr, da die Zurückweisung der Unionsmarke sämtliche nationalen Schutzmöglichkeiten sperrt.",
      "A kann direkt nationale Markenanmeldungen einreichen, verliert aber den Prioritätstag der ursprünglichen Unionsmarkenanmeldung.",
    ],
    answer: 1,
    explanation:
      "Art. 139 UMV: Bei Zurückweisung einer Unionsmarkenanmeldung kann der Anmelder die Umwandlung in nationale Anmeldungen beantragen — allerdings nur für Mitgliedstaaten, in denen das Zurückweisungshindernis nicht besteht (Art. 139 Abs. 2 UMV). Da das beschreibende Hindernis nur in Spanien und Portugal vorlag, kann A Umwandlungsanmeldungen für Deutschland, Frankreich und Österreich beantragen. Die nationalen Anmeldungen, die aus der Umwandlung hervorgehen, genießen denselben Anmeldetag wie die ursprüngliche Unionsmarkenanmeldung (Art. 141 Abs. 1 UMV).",
    sourceIds: ["umv139"],
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["unionsmarke", "umwandlung", "konversion", "art139-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-033",
    pathId,
    topic: "Art. 8 Abs. 1 lit. b UMV — Kennzeichnungskraft",
    title: "Wechselwirkung: starke Kennzeichnungskraft erweitert Schutzbereich",
    prompt:
      "Die ältere Unionsmarke 'VITA' besitzt durch intensive Benutzung für Vitaminpräparate eine gesteigerte Kennzeichnungskraft. Der Anmelder möchte 'VITALIS' für Vitaminpräparate eintragen. Wie wirkt die gesteigerte Kennzeichnungskraft der älteren Marke auf die Beurteilung der Verwechslungsgefahr?",
    options: [
      "Gesteigerte Kennzeichnungskraft hat keinen Einfluss auf die Verwechslungsgefahr; maßgeblich ist nur die Zeichenähnlichkeit.",
      "Gesteigerte Kennzeichnungskraft verengt den Schutzbereich — bei starken Marken ist die Verwechslungsgefahr leichter zu verneinen.",
      "Gesteigerte Kennzeichnungskraft erweitert den Schutzbereich — eine stärkere Marke genießt breiteren Schutz; auch bei geringerer Zeichenähnlichkeit kann Verwechslungsgefahr bejaht werden.",
      "Gesteigerte Kennzeichnungskraft führt automatisch zur Annahme von Verwechslungsgefahr.",
    ],
    answer: 2,
    explanation:
      "EuGH 'Canon' (C-39/97) und ständige EuG-Rechtsprechung: Eine durch intensive Benutzung erworbene gesteigerte Kennzeichnungskraft erweitert den Schutzbereich der älteren Marke. Im Wechselwirkungsmodell bedeutet dies: Auch bei einer geringeren Zeichenähnlichkeit (wie zwischen 'VITA' und 'VITALIS') kann Verwechslungsgefahr bejaht werden, wenn die ältere Marke überdurchschnittliche Kennzeichnungskraft besitzt. Gesteigerte Kennzeichnungskraft allein begründet allerdings keine automatische Verwechslungsgefahr — eine Gesamtabwägung bleibt erforderlich.",
    sourceIds: ["umv8"],
    levelNumber: 2,
    errorCategory: "Subsumtion",
    skillTags: ["unionsmarke", "verwechslungsgefahr", "kennzeichnungskraft", "wechselwirkung", "art8-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-034",
    pathId,
    topic: "Art. 7 Abs. 1 lit. j/k UMV — Geschützte geografische Angaben (Fallkonstellation)",
    title: "Fall: geografische Angabe als absolutes Eintragungshindernis",
    prompt:
      "Ein Weinproduzent möchte 'CHAMPAGNE ROYALE' als Unionsmarke für Schaumweine aus Deutschland eintragen. Das EUIPO lehnt ab. Auf welches absolute Hindernis stützt das EUIPO die Ablehnung am ehesten?",
    options: [
      "Art. 7 Abs. 1 lit. b UMV — fehlende Unterscheidungskraft",
      "Art. 7 Abs. 1 lit. j UMV — die Marke enthält eine geschützte Ursprungsbezeichnung (Champagne ist eine g.U. nach EU-Weinrecht) und soll für gleichartige Waren eingetragen werden, die diese Bezeichnung nicht erfüllen.",
      "Art. 7 Abs. 1 lit. c UMV — beschreibende geografische Angabe",
      "Art. 7 Abs. 1 lit. f UMV — Verstoß gegen die öffentliche Ordnung",
    ],
    answer: 1,
    explanation:
      "Art. 7 Abs. 1 lit. j UMV: Zeichen, die eine geschützte Ursprungsbezeichnung oder eine geschützte geografische Angabe nach den einschlägigen EU-Vorschriften enthalten oder aus einer solchen bestehen, sind von der Eintragung ausgeschlossen, wenn sie für gleichartige Waren angemeldet werden, die dieser Bezeichnung nicht entsprechen. 'Champagne' ist eine geschützte Ursprungsbezeichnung (g.U.) nach EU-Weinrecht (VO (EU) Nr. 1308/2013). Ein Schaumwein aus Deutschland kann diese g.U. nicht erfüllen — der Zusatz 'ROYALE' beseitigt das Hindernis nicht. Das Hindernis nach lit. j ist sachnäher als lit. c.",
    sourceIds: ["umv7"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["unionsmarke", "schutzhindernisse", "geografische-angabe", "art7-umv"],
    reviewStatus: "reviewed",
  }),

  // ─── ORDERING da-union-035 ────────────────────────────────────────────────

  makeOrdering(sources, {
    id: "da-union-035",
    pathId,
    topic: "Art. 30–46 UMV — Prüfungsschema EUIPO-Anmeldeverfahren",
    title: "Prüfungsschema: Unionsmarkenanmeldung beim EUIPO — korrekte Verfahrensabfolge",
    prompt:
      "Ordnen Sie die Verfahrensschritte einer Unionsmarkenanmeldung beim EUIPO in die korrekte zeitliche Reihenfolge.",
    orderItems: [
      "1. Einreichung der Anmeldung beim EUIPO (schriftlich oder elektronisch); Formalprüfung; Festlegung des Anmeldetags (Art. 32 UMV)",
      "2. Amtsprüfung durch das EUIPO: Prüfung der absoluten Eintragungshindernisse (Art. 7 UMV) von Amts wegen; ggf. vorläufige Ablehnung mit Möglichkeit zur Stellungnahme",
      "3. Erstellung des Recherchenberichts über frühere Unionsmarken; Übermittlung an Anmelder und betroffene ältere Markeninhaber (Art. 43 UMV)",
      "4. Veröffentlichung der Anmeldung im Blatt für Unionsmarken; Beginn der dreimonatigen Widerspruchsfrist (Art. 46 Abs. 1 UMV)",
      "5. Widerspruchsverfahren (sofern Widerspruch eingelegt wurde): Prüfung relativer Hindernisse (Art. 8 UMV); andernfalls direkte Eintragung",
      "6. Eintragung der Unionsmarke ins Register und Veröffentlichung der Eintragung",
    ],
    explanation:
      "Das EUIPO-Anmeldeverfahren gliedert sich in: (1) Einreichung und Formalprüfung (Anmeldetag, Gebühren, Waren-/DL-Verzeichnis). (2) Amtsprüfung der absoluten Hindernisse (Art. 7 UMV) — einziger Bereich, den das EUIPO von Amts wegen prüft. (3) Recherchenbericht (Art. 43 UMV) — informiert Anmelder und ältere Rechtsinhaber. (4) Veröffentlichung der Anmeldung — 3-Monats-Widerspruchsfrist (Art. 46 UMV) läuft an. (5) Widerspruchsverfahren (nur wenn Widerspruch eingelegt): Prüfung relativer Hindernisse. (6) Eintragung. Dieser Ablauf ist klausurrelevant, insb. die Abgrenzung Amtsprüfung (nur absolut) vs. Widerspruch (relativ).",
    sourceIds: ["umv7", "umv46"],
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["unionsmarke", "anmeldeverfahren", "euipo", "pruefungsschema"],
    reviewStatus: "reviewed",
  }),

  // ─── CLOZE da-union-036 ───────────────────────────────────────────────────

  makeCloze(sources, {
    id: "da-union-036",
    pathId,
    topic: "Art. 18 Abs. 1 UMV — Benutzungsfrist (Lückentext)",
    title: "Benutzungspflicht — Fristdauer und Fristbeginn (Cloze)",
    prompt:
      "Nach Art. 18 Abs. 1 UMV muss die Unionsmarke innerhalb von ___ nach dem Zeitpunkt der Eintragung ernsthaft in der Union benutzt werden.",
    options: [
      "drei Jahren",
      "fünf Jahren",
      "sieben Jahren",
      "zehn Jahren",
    ],
    answer: 1,
    explanation:
      "Art. 18 Abs. 1 UMV: Die Benutzungspflicht beginnt nach Ablauf von fünf Jahren ab der Eintragung der Unionsmarke. Erst nach Ablauf dieser Benutzungsschonfrist kann ein Antrag auf Verfall wegen Nichtbenutzung nach Art. 58 Abs. 1 lit. a UMV erfolgreich gestellt werden. Die Frist beginnt ab Eintragung — nicht ab Anmeldung.",
    sourceIds: ["umv18"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["unionsmarke", "benutzungspflicht", "fristdauer", "art18-umv"],
    reviewStatus: "reviewed",
  }),

  // ─── Weitere Fallkonstellationen da-union-037 … da-union-038 ─────────────

  makeSingle(sources, {
    id: "da-union-037",
    pathId,
    topic: "Art. 8 Abs. 1 lit. b UMV — Verwechslungsgefahr komplexer Fall (Fallkonstellation)",
    title: "Fall: Wechselwirkung — hohe Warenidentität bei mäßiger Zeichenähnlichkeit",
    prompt:
      "Die ältere Unionsmarke 'PRIMUS' (für Sportschuhe, normale Kennzeichnungskraft) trifft auf die angemeldete jüngere Marke 'PREMO' (ebenfalls für Sportschuhe). Das EUIPO bejaht Verwechslungsgefahr trotz nur mäßiger Zeichenähnlichkeit. Ist dies mit dem Wechselwirkungsprinzip vereinbar?",
    options: [
      "Nein — bei mäßiger Zeichenähnlichkeit kann Verwechslungsgefahr nie bejaht werden.",
      "Ja — nach dem Wechselwirkungsprinzip (EuGH 'Canon') kann hohe Warenähnlichkeit (hier: identische Waren) eine geringere Zeichenähnlichkeit kompensieren; die Gesamtabwägung kann dennoch Verwechslungsgefahr ergeben.",
      "Ja — bei Warenidentität wird Verwechslungsgefahr unwiderleglich vermutet.",
      "Nein — das Wechselwirkungsprinzip kompensiert nur Warenunähnlichkeit, nicht Zeichenunähnlichkeit.",
    ],
    answer: 1,
    explanation:
      "EuGH 'Canon' (C-39/97) und Art. 8 Abs. 1 lit. b UMV: Das Wechselwirkungsprinzip besagt ausdrücklich, dass ein geringer Grad der Ähnlichkeit der erfassten Waren oder Dienstleistungen durch einen höheren Grad der Ähnlichkeit der Marken ausgeglichen werden kann und umgekehrt. Im vorliegenden Fall: Warenidentität (sehr hohe Ähnlichkeit) kompensiert die mäßige Zeichenähnlichkeit zwischen 'PRIMUS' und 'PREMO' — beide Zeichen teilen den Anfang 'PRI-'/'PRE-' und die Endung '-US'/'-O', was klanglich Ähnlichkeit erzeugt. Warenidentität führt nicht zu einer unwiderleglichen Verwechslungsgefahr (das wäre der Tatbestand der Doppelidentität), aber die Gesamtabwägung fällt hier zugunsten des Widerspruchs aus.",
    sourceIds: ["umv8"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["unionsmarke", "verwechslungsgefahr", "wechselwirkung", "art8-umv"],
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-union-038",
    pathId,
    topic: "Art. 59 / 62 UMV — Bösgläubigkeit als Nichtigkeitsgrund (Fallkonstellation)",
    title: "Fall: Bösgläubige Anmeldung einer Unionsmarke — Wirkung der Nichtigkeit",
    prompt:
      "Unternehmen X meldet die Unionsmarke 'ELITEX' an, obwohl es weiß, dass Konkurrent Y das Zeichen seit Jahren im Ausland benutzt und Y die Marke in der EU einzutragen beabsichtigt. Y beantragt beim EUIPO absolute Nichtigkeit wegen bösgläubiger Anmeldung. Dem Antrag wird stattgegeben. Welche zeitliche Wirkung hat die Nichtigerklärung?",
    options: [
      "Die Unionsmarke von X gilt ab dem Zeitpunkt der Entscheidung des EUIPO als nichtig (ex nunc).",
      "Die Unionsmarke von X gilt als von Anfang an nicht eingetragen (ex tunc); ihre Wirkungen gelten unionsweit als nie eingetreten.",
      "Die Nichtigerklärung hat keine Rückwirkung, sondern nur Wirkung für die Zukunft.",
      "Die Nichtigkeit betrifft nur die Staaten, in denen Y die Benutzung nachgewiesen hat.",
    ],
    answer: 1,
    explanation:
      "Art. 59 Abs. 1 lit. b UMV (bösgläubige Anmeldung) i.V.m. Art. 62 Abs. 2 UMV: Wird eine Unionsmarke für nichtig erklärt, gilt sie als von Anfang an nicht mit den Wirkungen der UMV ausgestattet — ex tunc-Wirkung. Die Marke gilt rechtlich als nie eingetragen, sodass auch etwaige aus ihr abgeleitete Ansprüche des X entfallen. Ein eigener Schadensersatzanspruch des Y folgt daraus jedoch NICHT — er setzte ein eigenes älteres durchsetzbares Recht voraus, das hier gerade fehlt (Y hat das Zeichen nur im Ausland benutzt; gerade deshalb greift die absolute Bösgläubigkeits-Nichtigkeit nach Art. 59, nicht die relative Nichtigkeit nach Art. 60). Die Nichtigkeit wirkt einheitlich für das gesamte Unionsgebiet (Einheitsprinzip), nicht auf einzelne Mitgliedstaaten beschränkt.",
    sourceIds: ["umv59"],
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["unionsmarke", "boesglaeubigkeit", "nichtigkeit", "ex-tunc", "art59-umv"],
    // FIX (Orchestrator): falschen Schadensersatz-Zusatz in Option 1 UND Erklärung entfernt (ex-tunc-Nichtigkeit begründet keinen eigenen SE-Anspruch des Y ohne eigenes älteres Recht; daher Art. 59 statt Art. 60). Kern (answer 1: ex tunc + Einheitsprinzip) korrekt.
    reviewStatus: "reviewed",
  }),

];
